import { localUrl as appLocalUrl } from './app-path.js';
import axios from 'axios';
import SessionManager from './session-manager';
import SubscriptionSyncService from './services/subscription-sync-service';
import FeatureManager from './feature-manager';

const ApiClient = {
	KEYS: {
		DEVICE_ID: 'climatempo_device_id',
		SESSION_ID: 'climatempo_session_id',
	},
	axiosInstance: null,
	isRefreshing: false,
	failedQueue: [],

	generateIdentityId(prefix) {
		const randomPart =
			typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
				? crypto.randomUUID()
				: `${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;

		return `${prefix}_${randomPart}`;
	},

	getOrCreateDeviceId() {
		const existing = localStorage.getItem(this.KEYS.DEVICE_ID);
		if (existing && String(existing).trim()) {
			return existing;
		}

		const created = this.generateIdentityId('web');
		localStorage.setItem(this.KEYS.DEVICE_ID, created);
		return created;
	},

	getOrCreateSessionId() {
		const existing = sessionStorage.getItem(this.KEYS.SESSION_ID);
		if (existing && String(existing).trim()) {
			return existing;
		}

		const created = this.generateIdentityId('sess');
		sessionStorage.setItem(this.KEYS.SESSION_ID, created);
		return created;
	},

	attachIdentityHeaders(config = {}) {
		const deviceId = this.getOrCreateDeviceId();
		const sessionId = this.getOrCreateSessionId();

		config.headers = config.headers || {};
		config.headers['x-device-id'] = deviceId;
		config.headers['x-session-id'] = sessionId;
		return config;
	},

	attachNoCacheHeaders(config = {}) {
		config.headers = config.headers || {};
		config.headers['Cache-Control'] =
			'no-store, no-cache, must-revalidate, max-age=0';
		config.headers.Pragma = 'no-cache';
		return config;
	},

	shouldBypassHttpCache(url) {
		if (!url || typeof url !== 'string') {
			return false;
		}

		return (
			this.isBffUrl(url) ||
			url.startsWith(appLocalUrl('/v2/json/')) ||
			url.startsWith(appLocalUrl('/json/myclimatempo/')) ||
			url.includes('/profile') ||
			url.includes('/subscriptions') ||
			url.includes('/favorite-locales')
		);
	},

	appendCacheBuster(url) {
		if (!url || typeof url !== 'string' || url.includes('_=')) {
			return url;
		}

		return `${url}${url.includes('?') ? '&' : '?'}_=${Date.now()}`;
	},

	init() {
		if (this.axiosInstance) return;

		this.axiosInstance = axios.create({
			timeout:
				parseInt(process.env.REQUEST_TIMEOUT_MS, 10) ||
				parseInt(process.env.REQUEST_TIMEOUT, 10) ||
				30000,
			headers: {
				'Content-Type': 'application/json',
			},
		});

		this.setupInterceptors();
		SessionManager.ensureConsistentSession(window.ClimatempoAuth || null);
		SubscriptionSyncService.startAutoSync(this);

		if (process.env.DEBUG === 'true') {
			console.log(
				'✅ ApiClient inicializado com baseURL:',
				this.axiosInstance.defaults.baseURL,
			);
		}
	},

	setupInterceptors() {
		this.axiosInstance.interceptors.request.use(
			async config => {
				const requiresAuth = this.requiresAuth(config);
				const requestUrl = typeof config.url === 'string' ? config.url : '';
				const requestMethod = config.method
					? String(config.method).toLowerCase()
					: 'get';

				this.attachIdentityHeaders(config);
				this.attachNoCacheHeaders(config);

				if (
					requestMethod === 'get' &&
					this.shouldBypassHttpCache(requestUrl)
				) {
					config.url = this.appendCacheBuster(requestUrl);
				}

				if (this.isBffUrl(requestUrl)) {
					const apiKey = this.getBffApiKey();
					if (apiKey) {
						config.headers = config.headers || {};
						config.headers['x-api-key'] = apiKey;
					}
				}

				if (requiresAuth) {
					const token = SessionManager.getAccessToken();
					if (token) {
						config.headers = config.headers || {};
						config.headers.Authorization = `Bearer ${token}`;
					}
				}

				return config;
			},
			error => {
				return Promise.reject(error);
			},
		);

		this.axiosInstance.interceptors.response.use(
			response => response,
			async error => {
				const originalRequest = error.config;
				const requiresAuth = this.requiresAuth(originalRequest || {});
				const requestMethod = originalRequest?.method
					? String(originalRequest.method).toUpperCase()
					: 'UNKNOWN';
				const requestUrl = originalRequest?.url || '';

				if (
					error.response?.status === 401 &&
					requiresAuth &&
					originalRequest &&
					!originalRequest._retry
				) {
					if (this.isRefreshing) {
						return new Promise((resolve, reject) => {
							this.failedQueue.push({ resolve, reject });
						})
							.then(token => {
								originalRequest.headers.Authorization = `Bearer ${token}`;
								return this.axiosInstance(originalRequest);
							})
							.catch(err => {
								return Promise.reject(err);
							});
					}

					originalRequest._retry = true;
					this.isRefreshing = true;

					console.log('🔒 Token expirado, tentando refresh...');

					try {
						const newAccessToken =
							await SessionManager.refreshAccessToken(this);

						if (newAccessToken) {
							this.processQueue(null, newAccessToken);

							originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

							return this.axiosInstance(originalRequest);
						} else {
							this.processQueue(new Error('Falha ao atualizar token'), null);
							this.handleLogout();
							return Promise.reject(error);
						}
					} catch (refreshError) {
						console.error('Erro ao fazer refresh:', refreshError);
						this.processQueue(refreshError, null);
						this.handleLogout();
						return Promise.reject(refreshError);
					} finally {
						this.isRefreshing = false;
					}
				}

				if (error.response) {
					console.error('Erro HTTP:', {
						status: error.response.status,
						method: requestMethod,
						url: requestUrl,
						data: error.response.data,
					});
				} else if (error.request) {
					console.error('Erro de rede:', {
						message: error.message,
						method: requestMethod,
						url: requestUrl,
					});
				} else {
					console.error('Erro:', error.message);
				}

				return Promise.reject(error);
			},
		);
	},

	requiresAuth(config = {}) {
		return config.requiresAuth === true;
	},

	processQueue(error, token = null) {
		this.failedQueue.forEach(promise => {
			if (error) {
				promise.reject(error);
			} else {
				promise.resolve(token);
			}
		});

		this.failedQueue = [];
	},

	handleLogout() {
		console.log('🚪 Deslogando usuário...');
		SubscriptionSyncService.stopAutoSync();
		SubscriptionSyncService.clearSyncTimestamp();
		FeatureManager.clearCache();
		SessionManager.clearSession();

		// Redireciona para login
		window.location.href = appLocalUrl('/login');
	},

	handleError(error) {
		if (!error.response) {
			return {
				message:
					'Não foi possível conectar ao servidor. Verifique sua conexão.',
				originalData: null,
			};
		}

		const { status, data } = error.response;
		const serverMessage = data?.message || data?.error;

		let defaultMessage;
		switch (status) {
			case 400:
				defaultMessage = 'Requisição inválida. Verifique os dados enviados.';
				break;
			case 401:
				defaultMessage = 'Não autorizado. Faça login novamente.';
				break;
			case 403:
				defaultMessage = 'Você não tem permissão para acessar este recurso.';
				break;
			case 404:
				defaultMessage = 'Recurso não encontrado.';
				break;
			case 422:
				defaultMessage = 'Dados inválidos. Verifique os campos enviados.';
				break;
			case 429:
				defaultMessage =
					'Muitas requisições. Aguarde um momento e tente novamente.';
				break;
			case 500:
				defaultMessage =
					'Erro interno do servidor. Tente novamente mais tarde.';
				break;
			case 502:
				defaultMessage =
					'Servidor temporariamente indisponível. Tente novamente.';
				break;
			case 503:
				defaultMessage = 'Serviço indisponível. Tente novamente mais tarde.';
				break;
			default:
				defaultMessage = 'Erro desconhecido. Tente novamente.';
		}

		return {
			message: serverMessage || defaultMessage,
			originalData: data,
			status,
		};
	},

	buildErrorDetails(error) {
		const config = error?.config || {};
		const response = error?.response || null;

		return {
			message: this.handleError(error),
			status: response?.status || null,
			code: error?.code || null,
			method: config?.method ? String(config.method).toUpperCase() : null,
			url: config?.url || null,
			data: response?.data || null,
			isNetworkError: !response,
		};
	},

	async get(url, config = {}) {
		this.init();
		try {
			const response = await this.axiosInstance.get(url, config);
			return { success: true, data: response.data };
		} catch (error) {
			const errorDetails = this.buildErrorDetails(error);
			return { success: false, error: errorDetails.message, errorDetails };
		}
	},

	async post(url, data, config = {}) {
		this.init();
		try {
			const response = await this.axiosInstance.post(url, data, config);
			return { success: true, data: response.data };
		} catch (error) {
			const errorDetails = this.buildErrorDetails(error);
			return { success: false, error: errorDetails.message, errorDetails };
		}
	},

	async put(url, data, config = {}) {
		this.init();
		try {
			const response = await this.axiosInstance.put(url, data, config);
			return { success: true, data: response.data };
		} catch (error) {
			const errorDetails = this.buildErrorDetails(error);
			return { success: false, error: errorDetails.message, errorDetails };
		}
	},

	async patch(url, data, config = {}) {
		this.init();
		try {
			const response = await this.axiosInstance.patch(url, data, config);
			return { success: true, data: response.data };
		} catch (error) {
			const errorDetails = this.buildErrorDetails(error);
			return { success: false, error: errorDetails.message, errorDetails };
		}
	},

	async delete(url, config = {}) {
		this.init();
		try {
			const response = await this.axiosInstance.delete(url, config);
			return { success: true, data: response.data };
		} catch (error) {
			const errorDetails = this.buildErrorDetails(error);
			return { success: false, error: errorDetails.message, errorDetails };
		}
	},

	// BFF-specific methods
	async bffGet(path, config = {}) {
		const url = this.buildBffUrl(path);
		return this.get(url, this.withBffApiKey(config));
	},

	async bffPost(path, data, config = {}) {
		const url = this.buildBffUrl(path);
		return this.post(url, data, this.withBffApiKey(config));
	},

	async bffPut(path, data, config = {}) {
		const url = this.buildBffUrl(path);
		return this.put(url, data, this.withBffApiKey(config));
	},

	async bffPatch(path, data, config = {}) {
		const url = this.buildBffUrl(path);
		return this.patch(url, data, this.withBffApiKey(config));
	},

	async bffDelete(path, config = {}) {
		const url = this.buildBffUrl(path);
		return this.delete(url, this.withBffApiKey(config));
	},

	getToken() {
		return SessionManager.getAccessToken();
	},

	setToken(token) {
		SessionManager.updateAccessToken(token);
	},

	clearToken() {
		SessionManager.clearSession();
	},

	getBffBaseUrl() {
		return process.env.BFF_API_URL || 'http://localhost:4000';
	},

	getBffApiKey() {
		return process.env.BFF_API_KEY || '';
	},

	isBffUrl(url) {
		const bffBaseUrl = this.getBffBaseUrl().replace(/\/+$/, '');
		return !!url && url.startsWith(bffBaseUrl);
	},

	withBffApiKey(config = {}) {
		const apiKey = this.getBffApiKey();
		if (!apiKey) {
			return config;
		}

		return {
			...config,
			headers: {
				...(config.headers || {}),
				'x-api-key': apiKey,
			},
		};
	},

	buildBffUrl(path) {
		const baseUrl = this.getBffBaseUrl().replace(/\/+$/, '');
		const cleanPath = path.startsWith('/') ? path : `/${path}`;
		return `${baseUrl}${cleanPath}`;
	},
};

export default ApiClient;
